import { useState, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAppStore } from '@/store'
import { useProduccionDiariaData }    from '@/hooks/useProduccionDiariaData'
import { useProduccionDiariaFilters } from '@/hooks/useProduccionDiariaFilters'
import { useFiltrosPersistentes }     from '@/hooks/useFiltrosPersistentes'
import { useIsEmpresaAll }            from '@/hooks/useIsEmpresaAll'
import { useConsumosAgregado }        from '@/hooks/useConsumosAgregado'
import { ProduccionDiariaKpis }       from '@/components/produccionDiaria/ProduccionDiariaKpis'
import { PAGE_KPIS }                  from '@/config/uiFlags'
import { ProduccionDiariaFilters }    from '@/components/produccionDiaria/ProduccionDiariaFilters'
import { ProduccionDiariaChart }      from '@/components/produccionDiaria/ProduccionDiariaChart'
import { ProduccionDiariaTable }      from '@/components/produccionDiaria/ProduccionDiariaTable'
import { ProduccionDiariaPivot }      from '@/components/produccionDiaria/ProduccionDiariaPivot'
import { ConsumosInvertidoTable }     from '@/components/produccionDiaria/ConsumosInvertidoTable'
import { ConsumosFilters }            from '@/components/produccionDiaria/ConsumosFilters'
import { PageHeader }    from '@/components/common/PageHeader'
import { RefreshButton } from '@/components/common/RefreshButton'
import { useFmtArticulo } from '@/hooks/useFmtArticulo'
import { catalogoArticulos } from '@/utils/filtroArticulo'
import { desdeInicial } from '@/utils/rangoPorDefecto'

export default function ProduccionDiariaPage() {
  const { dark } = useAppStore()
  const { t } = useTranslation()
  const isEmpresaAll = useIsEmpresaAll()
  const fmtArt = useFmtArticulo()
  const [showChart, setShowChart] = useState(false)
  const [viewMode, setViewMode] = useState<'detalle' | 'consumos' | 'pivot'>('detalle')
  const [, setSearchParams] = useSearchParams()
  const [consumoQ, setConsumoQ] = useState<string[]>([])
  const [consumoFamilia, setConsumoFamilia] = useState('')

  // Los filtros de la pantalla aguantan irse a otra y volver, durante la sesión.
  // Se llama UNA vez aquí y se le pasan al hook de filtros: el rango de fechas es
  // de la página y el resto del hook, pero la query string es una sola.
  const { paramsIniciales: params0, olvidar } = useFiltrosPersistentes('produccionDiaria')

  // Sin rango por defecto: arranca vacío → la query trae todas las semanas.
  // Las fechas SÍ entran en lo que se recuerda: esta pantalla no calcula ningún
  // rango por defecto («sin rango» = todas las semanas), así que reponer el que
  // el usuario escribió no puede vaciarle la tabla contra un default que se
  // mueve solo. Y es el filtro más caro de volver a teclear.
  const [desde, setDesdeState] = useState(desdeInicial(params0.get('desde')))
  const [hasta, setHastaState] = useState(params0.get('hasta') ?? '')

  function setDesde(v: string) {
    setDesdeState(v)
    setSearchParams(p => { p.set('desde', v); return p }, { replace: true })
  }
  function setHasta(v: string) {
    setHastaState(v)
    setSearchParams(p => { p.set('hasta', v); return p }, { replace: true })
  }
  function clearAll() {
    // Limpiar = sin filtro de fecha → todas las semanas que devuelva la query.
    setDesdeState(''); setHastaState('')
    // El hook resetea su propio estado y, de paso, quita SUS claves de la URL.
    filters.clearFilters()
    // …y esta escritura va la última a propósito. La página y el hook llaman
    // cada uno a su `useSearchParams`, así que sus actualizadores parten de dos
    // instantáneas DISTINTAS de la query string: encadenarlos hacía que la
    // segunda resucitase lo que la primera acababa de borrar, y las fechas
    // sobrevivían a un «Limpiar todos» que dice que las quita. Una sola
    // escritura absoluta al final no puede perder esa carrera.
    setSearchParams({}, { replace: true })
    // La URL vacía ya borra el recuerdo por sí sola; `olvidar` lo hace explícito.
    olvidar()
  }

  const { rows, isLoading, isError, refetch, kpis, chartData } =
    useProduccionDiariaData(desde, hasta)

  const filters = useProduccionDiariaFilters(rows, params0)
  const totalActiveFilters = filters.activeFilters

  const consumos = useConsumosAgregado(desde, hasta, filters.empresa, viewMode === 'consumos')

  // Catálogo de componentes consumidos, para sugerir al escribir. No limita la
  // búsqueda: el campo sigue aceptando texto libre. Título formateado y valor
  // crudo, igual que en la tabla de consumos.
  const opcionesComponente = useMemo(
    () => catalogoArticulos(
      consumos.rows, c => c.componente, c => c.descripcionComponente, fmtArt,
    ),
    [consumos.rows, fmtArt],
  )

  // Lotes y órdenes de producción reales de lo cargado. Catálogo, no texto
  // libre: son dominios cerrados y escribir un lote a mano sólo produce ceros.
  const opcionesLote = useMemo(() => {
    const vistos = new Map<string, string>()
    for (const r of rows) {
      const l = (r.lote ?? '').trim()
      if (l && !vistos.has(l)) vistos.set(l, r.descripcion ?? '')
    }
    return [...vistos.entries()]
      .map(([lote, descripcion]) => ({ valor: lote, titulo: lote, detalle: descripcion }))
      .sort((a, b) => a.titulo.localeCompare(b.titulo, 'es-ES'))
  }, [rows])

  const opcionesOp = useMemo(() => {
    const vistos = new Map<string, string>()
    for (const r of rows) {
      if (r.numOp && !vistos.has(r.numOp)) vistos.set(r.numOp, r.descripcion ?? '')
    }
    return [...vistos.entries()]
      .map(([op, descripcion]) => ({ valor: op, titulo: op, detalle: descripcion }))
      .sort((a, b) => a.titulo.localeCompare(b.titulo, 'es-ES'))
  }, [rows])

  // Ojo con la asimetría de los tres catálogos de esta pantalla: SÓLO éste pasa
  // por el formateador. Lote y nº de OP no son códigos de artículo y recortarles
  // la primera letra los rompería.
  const opcionesArticulo = useMemo(
    () => catalogoArticulos(rows, r => r.articulo, r => r.descripcion, fmtArt),
    [rows, fmtArt],
  )

  const consumoFamilias = useMemo(
    () => [...new Set(consumos.rows.map(r => r.familiaComponente).filter(Boolean) as string[])].sort((a, b) => a.localeCompare(b, 'es-ES')),
    [consumos.rows],
  )

  const pageBg     = dark ? '-m-6 p-6 bg-slate-950 text-slate-100 min-h-full' : 'text-slate-900'
  const btnBase    = 'text-[11px] px-2.5 py-1.5 rounded-lg border transition-colors font-medium cursor-pointer'
  const btnNeutral = dark
    ? `${btnBase} border-white/10 bg-white/5 text-slate-300 hover:bg-white/10`
    : `${btnBase} border-slate-200 bg-white text-slate-500 hover:bg-slate-50 shadow-sm`
  const btnActive  = `${btnBase} bg-sky-500 border-sky-500 text-white`

  return (
    <div className={`flex flex-col gap-4 ${pageBg}`}>

      <PageHeader
        dark={dark}
        title={t('production.title')}
        subtitle={
          <>
            {t('production.subtitle')} · {filters.filtered.length} {t('common.of')} {rows.length} {t('common.records')}
            {totalActiveFilters > 0 && (
              <span className="ml-1 text-sky-500 font-semibold">
                · {t('common.activeFilter', { count: totalActiveFilters })}
              </span>
            )}
          </>
        }
        actions={
          <>
            {isError && (
              <span className="text-[11px] text-red-500 font-semibold">{t('common.error')}</span>
            )}
            <RefreshButton dark={dark} onClick={() => refetch()} />
            {PAGE_KPIS.production && (
              <button
                onClick={() => setShowChart(!showChart)}
                className={showChart ? btnActive : btnNeutral}
              >
                <svg className="w-3.5 h-3.5 inline mr-1" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                  <polyline points="1,13 5,7 9,10 13,3"/>
                </svg>
                {t('production.chart.modeTotal')}
              </button>
            )}
            {/* Toggle vista */}
            <div className={`flex rounded-lg border overflow-hidden ${dark ? 'border-white/10' : 'border-slate-200'}`}>
              <button
                onClick={() => setViewMode('detalle')}
                className={`${btnBase} rounded-none border-0 border-r ${dark ? 'border-white/10' : 'border-slate-200'} ${viewMode === 'detalle' ? btnActive : btnNeutral}`}
              >
                {t('production.tabDetail')}
              </button>
              <button
                onClick={() => setViewMode('consumos')}
                className={`${btnBase} rounded-none border-0 border-r ${dark ? 'border-white/10' : 'border-slate-200'} ${viewMode === 'consumos' ? btnActive : btnNeutral}`}
              >
                {t('production.tabConsumos')}
              </button>
              <button
                onClick={() => setViewMode('pivot')}
                className={`${btnBase} rounded-none border-0 ${viewMode === 'pivot' ? btnActive : btnNeutral}`}
              >
                {t('production.tabPivot')}
              </button>
            </div>
          </>
        }
      />

      {PAGE_KPIS.production && (
        <ProduccionDiariaKpis
          dark={dark}
          isLoading={isLoading}
          totalOps={kpis.totalOps}
          totalUds={kpis.totalUds}
          rendimiento={kpis.rendimiento}
          hoyOps={kpis.hoyOps}
          hoyUds={kpis.hoyUds}
          filteredCount={filters.filtered.length}
          totalCount={rows.length}
          rows={filters.filtered}
        />
      )}

      {showChart && !isLoading && chartData.length > 0 && (
        <ProduccionDiariaChart dark={dark} rows={filters.filtered} chartData={chartData} />
      )}

      {viewMode !== 'consumos' && (
        <ProduccionDiariaFilters
          dark={dark}
          desde={desde}
          hasta={hasta}
          q={filters.q}
          opcionesArticulo={opcionesArticulo}
          opcionesLote={opcionesLote}
          opcionesOp={opcionesOp}
          lote={filters.lote}
          numop={filters.numop}
          empresa={filters.empresa}
          estado={filters.estado}
          familia={filters.familia}
          rendimiento={filters.rendimiento}
          sortKey={filters.sortKey}
          sortDir={filters.sortDir}
          resumen={`${filters.filtered.length} ${t('common.of')} ${rows.length} ${t('common.records')}`}
          showEmpresaFilter={filters.showEmpresaFilter}
          allowedEmpresas={filters.allowedEmpresas}
          allEstados={filters.allEstados}
          allFamilias={filters.allFamilias}
          avp={filters.avp}
          setDesde={setDesde}
          setHasta={setHasta}
          setQ={filters.setQ}
          setLote={filters.setLote}
          setNumop={filters.setNumop}
          setEmpresa={filters.setEmpresa}
          setEstado={filters.setEstado}
          setFamilia={filters.setFamilia}
          setRendimiento={filters.setRendimiento}
          tipo={filters.tipo}
          setTipo={filters.setTipo}
          setSortKey={filters.setSortKey}
          setSortDir={filters.setSortDir}
          clearAll={clearAll}
        />
      )}

      {viewMode === 'consumos' && (
        <ConsumosFilters
          dark={dark}
          desde={desde} hasta={hasta}
          q={consumoQ}
          opcionesComponente={opcionesComponente}
          resumen={`${consumos.rows.length} ${t('common.records')}`} familia={consumoFamilia} familias={consumoFamilias}
          showEmpresaFilter={filters.showEmpresaFilter} empresa={filters.empresa} allowedEmpresas={filters.allowedEmpresas}
          setDesde={setDesde} setHasta={setHasta}
          setQ={setConsumoQ} setFamilia={setConsumoFamilia} setEmpresa={filters.setEmpresa}
        />
      )}

      {viewMode === 'detalle' ? (
        <ProduccionDiariaTable dark={dark} sorted={filters.sorted} sortKey={filters.sortKey} sortDir={filters.sortDir}
          isLoading={isLoading} isEmpresaAll={isEmpresaAll} setSortKey={filters.setSortKey} setSortDir={filters.setSortDir}
          desde={desde} hasta={hasta} empresa={filters.empresa} />
      ) : viewMode === 'consumos' ? (
        <ConsumosInvertidoTable dark={dark} desde={desde} hasta={hasta} empresa={filters.empresa} isEmpresaAll={isEmpresaAll}
          rows={consumos.rows} isLoading={consumos.isLoading} isError={consumos.isError} q={consumoQ} familia={consumoFamilia} />
      ) : (
        <ProduccionDiariaPivot dark={dark} rows={filters.filtered} />
      )}
    </div>
  )
}
